import { useMemo, useState } from "react";
import { AlertTriangle, BadgeCheck, CalendarClock, CalendarPlus, Hourglass, TimerOff } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { reportAnchor, reportRange, shiftReportAnchor, REPORT_PERIOD_OPTIONS, type ReportPeriod } from "../../lib/reportRange.js";
import { DropdownMultiSelect, DropdownSelect } from "../../components/DropdownButton.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import { Badge, type BadgeProps } from "../../components/ui/badge.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect,
  TableScrollStart, TableToolbar, dropdownProps, splitList, joinList, stickyName, stickyCode, NAME_WIDTH, CODE_WIDTH, type OpenState,
} from "../../components/reports/ReportKit.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";

type ProbationState = "onProbation" | "dueSoon" | "overdue" | "extended" | "confirmed" | "pip";
type FormState = "submitted" | "pending" | "notOpen";

interface ProbationRow {
  id: string; name: string; code: string; department: string | null; office: string | null; manager: string | null;
  status: string; hireDate: string | null; probationEnd: string | null; daysLeft: number | null;
  state: ProbationState; states: ProbationState[];
  extensions: number; decidedAt: string | null; decidedBy: string | null;
  form: FormState; formSubmittedAt: string | null;
}
interface ProbationReport {
  from: string; to: string;
  rows: ProbationRow[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: { onProbation: number; dueIn30: number; overdue: number; confirmed: number; extended: number; pip: number };
  meta: PaginationMeta;
}

const DEFAULTS = {
  search: "", officeIds: "", departmentIds: "", period: "month", anchor: "",
  states: "", page: "1", limit: "25",
};
// The cards cover everyone in scope; the status filter narrows only the table.
const ROW_ONLY = ["states"];

const API = "/reports/hr/probation";

const STATE_OPTIONS: { value: ProbationState; label: string }[] = [
  { value: "onProbation", label: "On probation" },
  { value: "dueSoon", label: "Due ≤ 14 days" },
  { value: "overdue", label: "Overdue" },
  { value: "extended", label: "Extended" },
  { value: "confirmed", label: "Confirmed" },
  { value: "pip", label: "Moved to PIP" },
];
const STATE_LABEL = Object.fromEntries(STATE_OPTIONS.map((o) => [o.value, o.label])) as Record<ProbationState, string>;

const STATE_TONE: Record<ProbationState, NonNullable<BadgeProps["tone"]>> = {
  onProbation: "info", dueSoon: "warning", overdue: "error", extended: "accent", confirmed: "success", pip: "error",
};

const FORM_TEXT: Record<FormState, string> = { submitted: "Yes", pending: "No", notOpen: "Not open yet" };
const FORM_TONE: Record<FormState, NonNullable<BadgeProps["tone"]>> = { submitted: "success", pending: "error", notOpen: "neutral" };

function daysLabel(r: ProbationRow): string {
  if (r.daysLeft === null) return "—";
  if (r.daysLeft < 0) return `${-r.daysLeft} d overdue`;
  return r.daysLeft === 0 ? "Today" : `${r.daysLeft} d`;
}

export default function ProbationReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const period = (REPORT_PERIOD_OPTIONS.some((o) => o.value === f.period) ? f.period : "month") as ReportPeriod;
  const anchor = f.anchor || reportAnchor(period);
  const range = reportRange(period, anchor);

  const params = useMemo(() => {
    const { period: _p, anchor: _a, ...rest } = f;
    return toParams(rest, { from: range.from, to: range.to });
  }, [f, range.from, range.to]);
  const { data, cards: c, loading, error } = useReport<ProbationReport>(API, params, "ProbationReportPage", ROW_ONLY);

  const rows = data?.rows ?? [];

  return (
    <ReportPage
      title="Probation Reports"
      description="Who is on probation, who is due or overdue for a decision, and the confirmations, extensions and PIPs decided in the period."
    >
      <CardGrid>
        <ReportStatCard icon={Hourglass} tone="brand" label="On Probation" value={c?.onProbation ?? "—"} caption="Right now" />
        <ReportStatCard icon={CalendarClock} tone="warning" label="Due in 30 Days" value={c?.dueIn30 ?? "—"} caption="Probation ends soon" />
        <ReportStatCard icon={TimerOff} tone="danger" label="Overdue" value={c?.overdue ?? "—"} caption="Past end date, no decision" />
        <ReportStatCard icon={BadgeCheck} tone="success" label="Confirmed" value={c?.confirmed ?? "—"} caption={range.label} />
        <ReportStatCard icon={CalendarPlus} tone="accent" label="Extended" value={c?.extended ?? "—"} caption={range.label} />
        <ReportStatCard icon={AlertTriangle} tone="rose" label="Moved to PIP" value={c?.pip ?? "—"} caption={range.label} />
      </CardGrid>

      <FilterBar>
        <SearchInput className="w-56" placeholder="Search name or code" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownMultiSelect
          values={splitList(f.states)} onChange={(v) => setF({ states: joinList(v) })} options={STATE_OPTIONS}
          allLabel="All statuses" ariaLabel="Probation status" {...dropdownProps("states", openState)}
        />
        <DropdownSelect
          value={period} onChange={(v) => setF({ period: v, anchor: "" })} options={REPORT_PERIOD_OPTIONS}
          placeholder="Period" ariaLabel="Decision period" clearable={false} {...dropdownProps("period", openState)}
        />
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, -1) })} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[150px] text-center text-sm font-semibold">{range.label}</div>
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor(period, anchor, 1) })} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
      </FilterBar>

      <TableToolbar left={<span className="text-[12px] text-surface-600">The period sets which confirmations, extensions and PIPs show. Everyone on probation now is always listed.</span>}>
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, `probation-${range.from}-to-${range.to}.xlsx`)} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText={`No employees match these filters for ${range.label}.`} contentKey={String(rows.length)}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              <TableHead className={cn(stickyName, "bg-surface-50")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
              <TableHead className={cn(stickyCode, "bg-surface-50")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Hire Date</TableHead>
              <TableHead>Probation End</TableHead>
              <TableHead className="text-center">Days Left</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Extensions</TableHead>
              <TableHead>Decision Date</TableHead>
              <TableHead>Decided By</TableHead>
              <TableHead className="text-center">30-day Form</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="group hover:bg-surface-50">
                <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
                  <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[r.name, r.department, r.office].filter(Boolean).join(" · ")}>{r.name}</div>
                </TableCell>
                <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
                  <div className={cn(CODE_WIDTH, "truncate")}>{r.code}</div>
                </TableCell>
                <TableCell className="whitespace-nowrap">{r.department ?? "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{r.manager ?? "—"}</TableCell>
                <TableCell className="whitespace-nowrap tabular-nums">{r.hireDate ?? "—"}</TableCell>
                <TableCell className="whitespace-nowrap tabular-nums">{r.probationEnd ?? "—"}</TableCell>
                <TableCell className={cn(
                  "whitespace-nowrap text-center tabular-nums",
                  r.daysLeft !== null && r.daysLeft < 0 && "font-semibold text-[var(--color-error-fg)]",
                  r.state === "dueSoon" && "font-semibold text-[var(--color-warning-fg)]",
                )}>
                  {daysLabel(r)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge tone={STATE_TONE[r.state]} title={r.states.map((s) => STATE_LABEL[s]).join(" · ")}>{STATE_LABEL[r.state]}</Badge>
                </TableCell>
                <TableCell className={cn("text-center tabular-nums", r.extensions > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{r.extensions}</TableCell>
                <TableCell className="whitespace-nowrap tabular-nums">{r.decidedAt ?? "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{r.decidedBy ?? "—"}</TableCell>
                <TableCell className="text-center">
                  <Badge tone={FORM_TONE[r.form]} title={r.formSubmittedAt ? `Submitted ${r.formSubmittedAt}` : undefined}>{FORM_TEXT[r.form]}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter meta={data?.meta} onPage={(p) => setF({ page: String(p) })} />
    </ReportPage>
  );
}
